/**
 * auth-actions.ts — wrappers testáveis para as funções do Firebase Auth SDK.
 *
 * Cada função faz lazy import de `firebase/auth` e `../lib/firebase` para
 * garantir compatibilidade com SSR (mesmo padrão do AuthContext.tsx).
 *
 * O acesso por e-mail é exclusivamente passwordless (Firebase Email Link). Não
 * há mais login/cadastro por senha: nenhuma função aqui usa
 * createUserWithEmailAndPassword / signInWithEmailAndPassword.
 */

import type { User, UserCredential } from 'firebase/auth';

async function getAuthAndFirebase() {
  const [firebaseAuth, { auth }] = await Promise.all([
    import('firebase/auth'),
    import('../lib/firebase'),
  ]);
  if (!auth) throw new Error('Firebase Auth not initialized');
  return { firebaseAuth, auth };
}

/**
 * Login passwordless (Firebase Email Link) — enviar o link de acesso.
 *
 * `continueUrl` deve ser uma URL da própria origem (mesma do app) e estar em
 * Authorized domains no Firebase Console. `handleCodeInApp: true` é obrigatório
 * para o fluxo de email link (o link é tratado pela própria SPA, não por uma
 * página externa). Funciona dentro de webview (não dispara o bloqueio de
 * user-agent do OAuth do Google).
 */
export async function sendEmailSignInLink(email: string, continueUrl: string): Promise<void> {
  const { firebaseAuth, auth } = await getAuthAndFirebase();
  await firebaseAuth.sendSignInLinkToEmail(auth, email, {
    url: continueUrl,
    handleCodeInApp: true,
  });
}

/** Confirma se `href` é um link de login passwordless válido do Firebase. */
export async function isEmailSignInLink(href: string): Promise<boolean> {
  const { firebaseAuth, auth } = await getAuthAndFirebase();
  return firebaseAuth.isSignInWithEmailLink(auth, href);
}

/** Conclui o login passwordless com o e-mail informado e a URL do link recebido. */
export async function completeEmailSignIn(email: string, href: string): Promise<UserCredential> {
  const { firebaseAuth, auth } = await getAuthAndFirebase();
  return firebaseAuth.signInWithEmailLink(auth, email, href);
}

/**
 * Retorna o usuário atual após a persistência do Firebase resolver
 * (`authStateReady`). Evita a corrida de ler `currentUser` antes da sessão ser
 * restaurada — usado para não reprocessar um link quando o usuário já está
 * autenticado no mesmo navegador.
 */
export async function getReadyCurrentUser(): Promise<User | null> {
  const { auth } = await getAuthAndFirebase();
  await auth.authStateReady();
  return auth.currentUser;
}
